import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { departmentAPI, userAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Layers, Plus, Eye, Trash2, Loader2, Search, Wallet } from 'lucide-react';
import { toast } from 'sonner';

const Departments = () => {
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    department_head_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [deptRes, userRes] = await Promise.all([
        departmentAPI.list(),
        userAPI.list()
      ]);
      setDepartments(deptRes.data);
      setUsers(userRes.data.filter(u => !u.department_id || u.role === 'ADMIN'));
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Department name is required');
      return;
    }
    
    setCreating(true);
    try {
      await departmentAPI.create({
        name: formData.name,
        description: formData.description,
        department_head_id: formData.department_head_id || null
      });
      toast.success('Department created successfully');
      setIsCreateOpen(false);
      setFormData({ name: '', description: '', department_head_id: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create department');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to deactivate "${name}"?`)) return;
    
    try {
      await departmentAPI.delete(id);
      toast.success('Department deactivated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate department');
    }
  };

  const filteredDepartments = departments.filter(d =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="departments-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">Departments</h1>
            <p className="text-gray-500 mt-1">Manage your organization's departments</p>
          </div>
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 text-gray-900 gap-2" data-testid="create-dept-btn">
                <Plus className="w-4 h-4" /> New Department
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader>
                <DialogTitle className="text-gray-900">Create Department</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">Department Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Marketing"
                    className="bg-gray-50 border-gray-300 text-gray-900"
                    data-testid="dept-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description"
                    className="bg-gray-50 border-gray-300 text-gray-900"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Department Head (Optional)</Label>
                  <Select
                    value={formData.department_head_id}
                    onValueChange={(val) => setFormData({ ...formData, department_head_id: val })}
                  >
                    <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900">
                      <SelectValue placeholder="Select a user" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-50 border-gray-300">
                      <SelectItem value="none" className="text-gray-500">None</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.user_id} value={user.user_id} className="text-gray-900">
                          {user.name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleCreate}
                  className="w-full bg-blue-900 hover:bg-blue-800 text-gray-900"
                  disabled={creating}
                  data-testid="create-dept-submit"
                >
                  {creating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating...</> : 'Create Department'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search departments..."
            className="pl-10 bg-white/50 border-gray-200 text-gray-900"
          />
        </div>

        {/* Departments Grid */}
        {filteredDepartments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDepartments.map((dept) => (
              <Card key={dept.department_id} className="glass-card hover:border-blue-600/20 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-900/10 flex items-center justify-center">
                      <Layers className="w-6 h-6 text-blue-600" />
                    </div>
                    <Badge className={dept.is_active ? 'status-success' : 'status-error'}>
                      {dept.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{dept.name}</h3>
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">{dept.description || 'No description'}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Balance</span>
                      <span className="text-gray-900 font-['JetBrains_Mono']">{formatCurrency(dept.balance)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Head</span>
                      <span className="text-gray-600">{dept.department_head_name || 'Unassigned'}</span>
                    </div>
                    {dept.virtual_account && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">UPI</span>
                        <span className="text-gray-500 font-['JetBrains_Mono'] text-xs">{dept.virtual_account.upi_id}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Link to={`/admin/departments/${dept.department_id}`} className="flex-1">
                      <Button variant="outline" className="w-full border-gray-300 text-gray-600 hover:bg-gray-50" size="sm">
                        <Eye className="w-4 h-4 mr-2" /> View
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      onClick={() => handleDelete(dept.department_id, dept.name)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="glass-card">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Layers className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">
                {searchTerm ? 'No departments match your search' : 'No departments created yet'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => setIsCreateOpen(true)}
                  className="bg-blue-900 hover:bg-blue-800 text-gray-900"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create First Department
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Departments;
